import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { ShieldCheck, Smartphone, Mail, KeyRound, Monitor, Copy, Download, Loader2 } from "lucide-react";

export const TwoFactorSettings = () => {
  const [st, setSt] = useState(null);
  const [pw, setPw] = useState("");
  const [method, setMethod] = useState("email");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [phone, setPhone] = useState("");
  const [phoneChallenge, setPhoneChallenge] = useState(null);
  const [phoneCode, setPhoneCode] = useState("");

  const load = useCallback(() => api.get("/auth/2fa/status").then((r) => { setSt(r.data); setMethod(r.data.method || "email"); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const needPw = () => { if (!pw) { toast.error("Enter your current password to confirm this change."); return false; } return true; };

  const enable = async () => {
    if (!needPw()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { password: pw, method });
      setCodes(data.recovery_codes);
      toast.success("Two-factor authentication enabled.");
      setPw(""); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const disable = async () => {
    if (!needPw()) return;
    setBusy(true);
    try { await api.post("/auth/2fa/disable", { password: pw }); toast.success("Two-factor authentication disabled."); setPw(""); setCodes(null); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const changeMethod = async (m) => {
    if (!needPw()) { setMethod(st.method); return; }
    setBusy(true);
    try { await api.post("/auth/2fa/method", { password: pw, method: m }); toast.success("Preferred method updated."); setPw(""); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); setMethod(st.method); } finally { setBusy(false); }
  };
  const regen = async () => {
    if (!needPw()) return;
    setBusy(true);
    try { const { data } = await api.post("/auth/recovery/regenerate", { password: pw }); setCodes(data.recovery_codes); toast.success("New recovery codes generated. Old ones no longer work."); setPw(""); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const startPhone = async () => {
    if (!needPw()) return;
    setBusy(true);
    try { const { data } = await api.post("/auth/phone/start", { password: pw, phone }); setPhoneChallenge(data); toast.success(`Code sent to ${data.destination}`); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const verifyPhone = async () => {
    setBusy(true);
    try { await api.post("/auth/phone/verify", { challenge_token: phoneChallenge.challenge_token, code: phoneCode }); toast.success("Mobile number verified."); setPhoneChallenge(null); setPhoneCode(""); setPhone(""); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const revokeDevice = async (id) => {
    try { await api.post("/auth/trusted-devices/revoke", { id }); toast.success("Trusted device revoked."); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const setEnforcement = async (v) => {
    try { await api.post("/auth/tenant/require-2fa", { require_2fa: v }); toast.success(v ? "2FA now required for all staff." : "Org-wide 2FA requirement turned off."); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const copyCodes = () => { navigator.clipboard.writeText(codes.join("\n")); toast.success("Recovery codes copied."); };
  const downloadCodes = () => {
    const blob = new Blob([`ChitChat CRM recovery codes\nKeep these safe — each can be used once.\n\n${codes.join("\n")}`], { type: "text/plain" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "chitchat-recovery-codes.txt"; a.click();
  };

  if (!st) return <div className="py-4 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading security…</div>;

  return (
    <div className="space-y-4" data-testid="twofa-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#00C9E0]" />
          <span className="font-medium text-slate-800">Two-Factor Authentication</span>
          <Badge variant={st.enabled ? "default" : "secondary"} data-testid="twofa-status-badge">{st.enabled ? "On" : "Off"}</Badge>
        </div>
      </div>

      {codes && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="recovery-codes-box">
          <p className="text-xs font-semibold text-amber-800">Save your recovery codes — shown only once. Each can be used once if you lose access to your 2FA method.</p>
          <div className="mt-2 grid grid-cols-2 gap-1 font-mono text-sm text-slate-800">
            {codes.map((c) => <span key={c}>{c}</span>)}
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={copyCodes} data-testid="copy-codes"><Copy className="h-3.5 w-3.5" />Copy</Button>
            <Button size="sm" variant="outline" onClick={downloadCodes} data-testid="download-codes"><Download className="h-3.5 w-3.5" />Download</Button>
          </div>
        </div>
      )}

      {/* Method selection */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-600">Verification method</Label>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => { setMethod("email"); if (st.enabled) changeMethod("email"); }}
            className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${method === "email" ? "border-[#00C9E0] bg-[#00C9E0]/5" : "border-slate-200"}`} data-testid="method-email">
            <Mail className="h-4 w-4 text-[#00C9E0]" /> <div className="text-left"><div className="font-medium">Email</div><div className="text-[11px] text-slate-400">{st.email}</div></div>
          </button>
          <button type="button" disabled={!st.sms_available}
            onClick={() => { if (!st.sms_available) return; setMethod("sms"); if (st.enabled) changeMethod("sms"); }}
            className={`flex items-center gap-2 rounded-lg border p-3 text-sm ${method === "sms" ? "border-[#00C9E0] bg-[#00C9E0]/5" : "border-slate-200"} ${!st.sms_available ? "opacity-60 cursor-not-allowed" : ""}`} data-testid="method-sms">
            <Smartphone className="h-4 w-4 text-[#00C9E0]" />
            <div className="text-left"><div className="font-medium">SMS</div>
              <div className="text-[11px] text-slate-400">{st.sms_available ? (st.phone_verified ? st.phone : "Verify a number") : "Setup Required"}</div></div>
          </button>
        </div>
        {!st.sms_available && <p className="text-[11px] text-slate-400">SMS text codes require a connected provider (Twilio). Ask your administrator to configure it — Email works today.</p>}
      </div>

      {/* Password confirm field for sensitive actions */}
      <div>
        <Label className="text-xs text-slate-600">Current password (to confirm changes)</Label>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1" placeholder="••••••••" data-testid="twofa-password" />
      </div>

      <div className="flex flex-wrap gap-2">
        {!st.enabled ? (
          <Button size="sm" onClick={enable} disabled={busy} data-testid="twofa-enable-btn">Enable 2FA</Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={regen} disabled={busy} data-testid="twofa-regen-btn"><KeyRound className="h-3.5 w-3.5" />Regenerate recovery codes ({st.recovery_codes_remaining})</Button>
            <Button size="sm" variant="destructive" onClick={disable} disabled={busy || st.require_2fa} data-testid="twofa-disable-btn">Disable 2FA</Button>
            {st.require_2fa && <span className="text-[11px] text-slate-400 self-center">Required by your administrator</span>}
          </>
        )}
      </div>

      {/* Phone verification (only when SMS provider connected) */}
      {st.sms_available && !st.phone_verified && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2">
          <Label className="text-xs text-slate-600">Verify a mobile number for SMS</Label>
          {!phoneChallenge ? (
            <div className="flex gap-2">
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+16045551234" data-testid="phone-input" />
              <Button size="sm" onClick={startPhone} disabled={busy} data-testid="phone-start-btn">Send code</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} placeholder="6-digit code" data-testid="phone-code-input" />
              <Button size="sm" onClick={verifyPhone} disabled={busy} data-testid="phone-verify-btn">Verify</Button>
            </div>
          )}
        </div>
      )}

      {/* Trusted devices */}
      <Separator />
      <div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Monitor className="h-4 w-4 text-slate-500" /><span className="text-sm font-medium text-slate-800">Trusted devices</span></div>
          {st.trusted_devices.length > 0 && <Button size="sm" variant="ghost" onClick={() => revokeDevice("all")} data-testid="revoke-all-devices">Revoke all</Button>}
        </div>
        {st.trusted_devices.length === 0 ? (
          <p className="text-xs text-slate-400 mt-1">No trusted devices. Checking “Remember this device” at sign-in skips the 2FA step on that browser for 30 days.</p>
        ) : (
          <ul className="mt-2 space-y-1">
            {st.trusted_devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between text-xs text-slate-600 border border-slate-100 rounded px-2 py-1.5" data-testid={`trusted-device-${d.id}`}>
                <span className="truncate max-w-[70%]">{d.label}</span>
                <button onClick={() => revokeDevice(d.id)} className="text-[#C42607] hover:underline">Revoke</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Admin: org-wide enforcement */}
      {st.is_admin && (
        <>
          <Separator />
          <div className="flex items-center justify-between" data-testid="enforce-2fa-row">
            <div>
              <p className="text-sm font-medium text-slate-800">Require 2FA for all staff</p>
              <p className="text-xs text-slate-400">Everyone in ChitChat Local must complete 2FA at sign-in.</p>
            </div>
            <Switch checked={st.require_2fa} onCheckedChange={setEnforcement} data-testid="enforce-2fa-switch" />
          </div>
        </>
      )}
    </div>
  );
};
